import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@alphabait/ui' // card'
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@alphabait/ui' // chart'
import { BarChart, Bar, XAxis, YAxis, LabelList } from 'recharts'
import { useTsrClient } from '../../end-users/tsr'
import { Info } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@alphabait/ui' // tooltip'
import { useTranslation } from 'react-i18next'
import { ChartLoadingState } from '@/components/charts/question-renderers/shared/chart-loading-state'
import { ChartEmptyState } from '@/components/charts/question-renderers/shared/chart-empty-state'

interface EngagementMetric {
  label: string
  count: number
  percentage?: number
}

interface EngagementRateProps {
  periodStart: string
  periodEnd: string
  customerId?: string
  campaignIds?: string[]
  campaignGroupId?: string
}

export function EngagementRate({
  periodStart,
  periodEnd,
  customerId,
  campaignIds,
  campaignGroupId,
}: EngagementRateProps) {
  const { t } = useTranslation()
  const tsr = useTsrClient()

  // Convert ISO dates to timestamps
  const startDate = Math.floor(new Date(periodStart).getTime() / 1000)
  const endDate = Math.floor(new Date(periodEnd).getTime() / 1000)

  const { data: engagementMetricsData, isPending: loadingEngagement } =
    tsr.dashboard.getEngagementMetrics.useQuery({
      queryKey: [
        'engagementMetrics',
        startDate,
        endDate,
        customerId,
        campaignIds,
        campaignGroupId,
      ],
      queryData: {
        query: {
          startDate,
          endDate,
          customerId,
          campaignIds,
          campaignGroupId,
        },
      },
    })

  const engagementMetrics = engagementMetricsData?.body || {
    qrEmailOpens: 0,
    giftSelection: 0,
    questionsAnswered: 0,
    claimedGift: 0,
    ratingsGiven: 0,
    sharedReferrals: 0,
    quickResponse: 0,
    totalAudience: 0,
    qrEmailOpensPercentage: 0,
    giftSelectionPercentage: 0,
    questionsAnsweredPercentage: 0,
    claimedGiftPercentage: 0,
    ratingsGivenPercentage: 0,
    sharedReferralsPercentage: 0,
    quickResponsePercentage: 0,
  }

  // Funnel chart: each step's % is relative to the previous step (drop-off)
  const engagementData: EngagementMetric[] = [
    {
      label: t('dashboard.engagement.qr_email_opens'),
      count: engagementMetrics.qrEmailOpens,
      percentage: Number(engagementMetrics.qrEmailOpensPercentage),
    },
    {
      label: t('dashboard.engagement.gift_selection'),
      count: engagementMetrics.giftSelection,
      percentage: Number(engagementMetrics.giftSelectionPercentage),
    },
    {
      label: t(
        'dashboard.engagement.started_questionnaire',
        'Started Questionnaire',
      ),
      count: engagementMetrics.questionsAnswered,
      percentage: Number(engagementMetrics.questionsAnsweredPercentage),
    },
    {
      label: t('dashboard.engagement.claimed_gift', 'Claimed Gift'),
      count: engagementMetrics.claimedGift,
      percentage: Number(engagementMetrics.claimedGiftPercentage),
    },
    {
      label: t('dashboard.engagement.ratings_given'),
      count: engagementMetrics.ratingsGiven,
      percentage: Number(engagementMetrics.ratingsGivenPercentage),
    },
    {
      label: t('dashboard.engagement.shared_referrals'),
      count: engagementMetrics.sharedReferrals,
      percentage: Number(engagementMetrics.sharedReferralsPercentage),
    },
    {
      label: t('dashboard.engagement.quick_response'),
      count: engagementMetrics.quickResponse,
      percentage: Number(engagementMetrics.quickResponsePercentage),
    },
  ]

  // End-to-end conversion: % of total audience that claimed a gift
  const overallScore = Number(
    (
      ((engagementMetrics.claimedGift ?? 0) /
        Math.max(engagementMetrics.totalAudience, 1)) *
      100
    ).toFixed(2),
  )

  // Format date range for display
  const displayDateRange = {
    start: new Date(periodStart).toLocaleDateString('en-US', { month: 'long' }),
    end: new Date(periodEnd).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    }),
  }

  const chartConfig: ChartConfig = {
    count: {
      label: 'Count',
      color: 'hsl(var(--base-chart-2))', // Green color
    },
  }

  const maxCount = Math.max(...engagementData.map((item) => item.count ?? 0))

  if (loadingEngagement) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">
            {t('dashboard.engagement.title')}
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            {t('dashboard.engagement.loading')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="h-[280px] flex items-center justify-center">
            <ChartLoadingState
              message={t('dashboard.engagement.loading_data')}
            />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (engagementData.every((item) => item.count === 0)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">
            {t('dashboard.engagement.title')}
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            {displayDateRange.start} - {displayDateRange.end}
          </CardDescription>
        </CardHeader>
        <CardContent className="h-96 flex items-center justify-center">
          <ChartEmptyState
            message={t('dashboard.engagement.no_data_available')}
          />
        </CardContent>
      </Card>
    )
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">
          {t('dashboard.engagement.title')}
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground">
          {displayDateRange.start} - {displayDateRange.end} ·{' '}
          {engagementMetrics.totalAudience.toLocaleString()}{' '}
          {t('dashboard.engagement.total_users', 'total users')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Horizontal Bar Chart */}
        <ChartContainer config={chartConfig} className="h-[360px]">
          <BarChart
            accessibilityLayer
            data={engagementData.map((item, index) => {
              const count = item.count ?? 0
              // Calculate minimum width needed for text (approximation)
              const textWidth = count.toString().length * 8 + 50 // rough calculation
              const minBarWidth = Math.max(textWidth, 150) // minimum 150px for readability

              // Convert to data units (reverse engineer the pixel-to-data ratio)
              const chartWidth = 800 // approximate chart width
              const minDataValue = (minBarWidth / chartWidth) * maxCount

              // Create a baseline + proportional system - increased baseline for longer bars
              const baselineValue = maxCount * 0.2 // 20% baseline for all bars (increased from 10%)
              const proportionalValue = (count / maxCount) * (maxCount * 0.8) // 80% proportional

              return {
                ...item,
                displayCount: Math.max(
                  minDataValue,
                  baselineValue + proportionalValue,
                ),
                fill:
                  index % 2 === 0
                    ? 'var(--base-chart-2)'
                    : 'var(--base-chart-4)',
              }
            })}
            layout="vertical"
            margin={{ top: 10, right: 100, bottom: 10, left: 10 }}
          >
            <XAxis
              type="number"
              dataKey="displayCount"
              hide
              domain={[0, maxCount * 1.2]}
            />
            <YAxis
              dataKey="label"
              type="category"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              width={120}
              fontSize={12}
              hide
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  hideLabel
                  formatter={(_, __, item) => {
                    const entry = item.payload
                    return `${entry.count} (${entry.percentage}%)`
                  }}
                />
              }
            />
            <Bar dataKey="displayCount" radius={12} barSize={50}>
              <LabelList
                dataKey="label"
                position="insideLeft"
                offset={12}
                className="fill-secondary-foreground font-medium"
                fontSize={12}
              />
              <LabelList
                dataKey={(entry) => `${entry.count} (${entry.percentage}%)`}
                position="right"
                offset={12}
                className="fill-secondary-foreground font-semibold"
                fontSize={12}
              />
            </Bar>
          </BarChart>
        </ChartContainer>

        {/* Overall Engagement Score */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('dashboard.engagement.overall_score')}
              </h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-gray-400 hover:text-secondary-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <div className="space-y-2">
                      <p className="font-medium">
                        {t('dashboard.engagement.overall_score')}
                      </p>
                      <p className="text-sm">
                        {t('dashboard.engagement.score_description')}
                      </p>
                      <p className="text-sm text-secondary-foreground">
                        <strong>
                          {t('dashboard.engagement.calculation_label')}
                        </strong>{' '}
                        {t('dashboard.engagement.calculation_description')}
                      </p>
                      <p className="text-sm text-secondary-foreground">
                        <strong>
                          {t('dashboard.engagement.higher_score_label')}
                        </strong>{' '}
                        {t('dashboard.engagement.higher_score_description')}
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-green-100">
                {overallScore}%
              </span>
            </div>
          </div>

          {/* Progress bar for overall score */}
          <div className="w-full bg-light-blue-200 rounded-full h-3 mb-2">
            <div
              className="bg-green-100 h-3 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(overallScore, 100)}%` }} //TODO: check is this calulated in % or just a number
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Export as default for easier importing
export default EngagementRate
